import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, CheckCircle, Users, Clock, Loader2 } from 'lucide-react';
import { formatLocalDate } from '@/lib/date';
import { toast } from '@/components/ui/sonner';
import { TeamRole } from '@/types/roles';

interface TeamInvitation {
  id: string;
  teamId: string;
  email: string;
  role: TeamRole;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  createdAt: string;
  teamName: string;
  inviterName: string;
  inviterEmail: string;
}

// Helper function for API calls
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const defaultOptions: RequestInit = {
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  };

  // Add session token to headers if it exists
  const accessToken = localStorage.getItem('sb-access-token');
  const refreshToken = localStorage.getItem('sb-refresh-token');
  
  if (accessToken && refreshToken) {
    defaultOptions.headers = {
      ...defaultOptions.headers,
      'Authorization': `Bearer ${accessToken}`,
      'X-Refresh-Token': refreshToken
    };
  }

  const API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'API request failed');
  }

  return response.json();
};

const InvitePage = () => {
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  
  const [invitation, setInvitation] = useState<TeamInvitation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('signin');
  
  // Form states
  const [signInForm, setSignInForm] = useState({
    email: '',
    password: ''
  });
  
  const [signUpForm, setSignUpForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organisationName: ''
  });

  const loadInvitation = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiCall(`/invites/${inviteId}`);
      const invite = response.data;
      
      // Check if invitation is expired
      const now = new Date();
      const expiresAt = new Date(invite.expiresAt);
      
      if (now > expiresAt) {
        setError('This invitation has expired');
        return;
      }

      if (invite.status !== 'pending') {
        setError('This invitation is no longer valid');
        return;
      }

      setInvitation(invite);
      
      // Pre-fill email in both forms
      setSignInForm(prev => ({ ...prev, email: invite.email }));
      setSignUpForm(prev => ({ ...prev, email: invite.email }));
      
    } catch (err) {
      console.error('Error loading invitation:', err);
      if (err instanceof Error && err.message.includes('not found')) {
        setError('Invitation not found or has expired');
      } else {
        setError('Failed to load invitation details');
      }
    } finally {
      setIsLoading(false);
    }
  }, [inviteId]);

  useEffect(() => {
    if (inviteId) {
      loadInvitation();
    }
  }, [inviteId, loadInvitation]);

  const signInWithSupabase = async (email: string, password: string) => {
    // Call the backend auth endpoint
    const response = await apiCall('/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // Store tokens
    const { accessToken, refreshToken } = response.data;
    localStorage.setItem('sb-access-token', accessToken);
    localStorage.setItem('sb-refresh-token', refreshToken);

    return response.data;
  };

  const signUpWithSupabase = async (email: string, password: string, name: string, organisationName?: string) => {
    // Call the backend auth endpoint
    const response = await apiCall('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ 
        email, 
        password, 
        name,
        organisationName: organisationName || undefined,
        teamId: invitation?.teamId,
        inviteId: invitation?.id
      }),
    });

    // Store tokens
    const { accessToken, refreshToken } = response.data;
    localStorage.setItem('sb-access-token', accessToken);
    localStorage.setItem('sb-refresh-token', refreshToken);

    return response.data;
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!signInForm.email || !signInForm.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setIsProcessing(true);
      
      // Sign in using backend API
      await signInWithSupabase(signInForm.email, signInForm.password);
      
      // Accept the invitation (for existing users)
      await acceptInvitation();
    } catch (err) {
      console.error('Sign in error:', err);
      toast.error('Sign in failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!signUpForm.name || !signUpForm.email || !signUpForm.password || !signUpForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    // Check organisation name is provided when not joining via invitation
    if (!invitation && !signUpForm.organisationName.trim()) {
      toast.error('Organisation name is required');
      return;
    }

    if (signUpForm.password !== signUpForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (signUpForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setIsProcessing(true);
      
      // Sign up using backend API (invitation acceptance is handled automatically)
      await signUpWithSupabase(signUpForm.email, signUpForm.password, signUpForm.name, signUpForm.organisationName);
      
      const successMessage = invitation 
        ? 'Account created and invitation accepted successfully! Please check your email to verify your account.'
        : 'Account and organization created successfully! Please check your email to verify your account.';
      
      toast.success(successMessage);
      
      // Redirect to email verification pending page
      navigate('/auth/email-verification-pending');
      
    } catch (err) {
      console.error('Sign up error:', err);
      toast.error('Sign up failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const acceptInvitation = async () => {
    try {
      await apiCall(`/invites/${inviteId}/accept`, {
        method: 'POST',
      });

      toast.success('Invitation accepted successfully!');
      
      // Redirect to dashboard
      navigate('/');
      
    } catch (err) {
      console.error('Error accepting invitation:', err);
      toast.error('Failed to accept invitation: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const formatDate = (dateString: string) => formatLocalDate(dateString, { year: 'numeric', month: 'long', day: 'numeric' });

  const getDaysUntilExpiry = () => {
    if (!invitation) return 0;
    const now = new Date();
    const expiresAt = new Date(invitation.expiresAt);
    const diffTime = expiresAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No invitation found
  if (!invitation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invitation Not Found</h2>
            <p className="text-muted-foreground mb-6">The invitation link appears to be invalid.</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Invitation Details Card */}
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl">You're Invited!</CardTitle>
            <CardDescription>
              Join <strong>{invitation.teamName}</strong> on Recallio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Team:</span>
                <span className="font-medium">{invitation.teamName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Invited by:</span>
                <span className="font-medium">{invitation.inviterName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Role:</span>
                <Badge variant={invitation.role === 'admin' ? 'default' : 'secondary'}>
                  {invitation.role}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Expires:</span>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {getDaysUntilExpiry()} days ({formatDate(invitation.expiresAt)})
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sign In/Sign Up Card */}
        <Card>
          <CardHeader>
            <CardTitle>Join the Team</CardTitle>
            <CardDescription>
              Sign in to your existing account or create a new one to accept the invitation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      value={signInForm.email}
                      onChange={(e) => setSignInForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      value={signInForm.password}
                      onChange={(e) => setSignInForm(prev => ({ ...prev, password: e.target.value }))}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isProcessing}>
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing In...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Sign In & Accept Invitation
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      value={signUpForm.name}
                      onChange={(e) => setSignUpForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={signUpForm.email}
                      onChange={(e) => setSignUpForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>
                  {!invitation && (
                    <div className="space-y-2">
                      <Label htmlFor="signup-organisation">Organisation Name</Label>
                      <Input
                        id="signup-organisation"
                        type="text"
                        value={signUpForm.organisationName}
                        onChange={(e) => setSignUpForm(prev => ({ ...prev, organisationName: e.target.value }))}
                        placeholder="Enter your organization name"
                        required
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={signUpForm.password}
                      onChange={(e) => setSignUpForm(prev => ({ ...prev, password: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                    <Input
                      id="signup-confirm-password"
                      type="password"
                      value={signUpForm.confirmPassword}
                      onChange={(e) => setSignUpForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isProcessing}>
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        {invitation ? 'Create Account & Accept Invitation' : 'Create Account & Organization'}
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvitePage;
